import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const TeacherCompositions = () => {
  const navigate = useNavigate();
  const [compositions, setCompositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');

  useEffect(() => {
    fetchCompositions();
  }, []);

  const fetchCompositions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/teacher/compositions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompositions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; }
  };

  const classes = [...new Set(compositions.map(c => c.class_name).filter(Boolean))];

  const filtered = compositions.filter((c) => {
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      if (
        !(c.title || '').toLowerCase().includes(t) &&
        !(c.subject || '').toLowerCase().includes(t) &&
        !(c.class_name || '').toLowerCase().includes(t)
      ) return false;
    }
    if (filterClass !== 'all' && c.class_name !== filterClass) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">🧪 Mes compositions</h1>
        <p className="text-gray-500">Compositions planifiées pour vos classes</p>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Les compositions sont planifiées par l'administration.
          Vous pouvez saisir les notes après la composition.
        </p>
      </div>

      {compositions.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="🔍 Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
            />
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
            >
              <option value="all">Toutes mes classes</option>
              {classes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="text-sm text-gray-500 flex items-center px-2">
              {filtered.length} composition(s)
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucune composition</p>
          <p className="text-sm text-gray-500">
            {compositions.length === 0
              ? "Aucune composition n'a été planifiée pour vos classes."
              : 'Aucun résultat pour ces filtres.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white rounded-xl shadow-md overflow-hidden border-l-4 border-[#E67E22]">
              <div className="p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-[#2C3E50]">{c.title}</h3>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        🟢 Programmée
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                      <div>🏫 <strong>{c.class_name || '-'}</strong></div>
                      <div>📚 <strong>{c.subject || '-'}</strong></div>
                      <div>📅 <strong>{formatDate(c.date)}</strong></div>
                      <div>⏰ <strong>{c.start_time && c.end_time ? `${c.start_time} - ${c.end_time}` : '-'}</strong></div>
                      {c.room && <div>🏛️ Salle : <strong>{c.room}</strong></div>}
                      <div>⚖️ Coef : <strong>{c.coefficient}</strong></div>
                    </div>
                    {c.topics && (
                      <div className="mt-3 bg-orange-50 rounded-lg p-3">
                        <p className="text-xs font-bold text-[#E67E22] mb-1">📖 Programme :</p>
                        <p className="text-xs text-gray-700">{c.topics}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => navigate('/teacher/grades')}
                      className="text-xs bg-[#E67E22] text-white px-3 py-2 rounded-lg hover:bg-[#D35400] transition font-medium whitespace-nowrap"
                    >
                      ✏️ Saisir les notes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherCompositions;